// ============================================================
//  SaveManager — versioned local save with V5 migration
// ============================================================
export const SAVE_VERSION = 6;
export const SAVE_KEY = 'cosmo_adventure_v6';
export const V5_KEY = 'ccsd_g2_adventure';

export interface SkillMastery {
  attempts: number;
  correct: number;
  recent: boolean[];      // last 10
  mastery: number;        // 0-100
  lastPracticed: number;  // timestamp
}

export interface DiscoveryLabWorldProgress {
  fieldCampusDiscovered: boolean;
  washedOutTrailDiscovered: boolean;
  investigationsCompleted: string[];
  washedOutTrailRestoration: 'damaged' | 'stage-1';
  unlockedDistricts: string[];
  investigationsStarted?: string[];
  pollinatorGardenDiscovered?: boolean;
  pollinatorGardenRestoration?: 'needs-pollinators' | 'restored';
  relationshipInvestigationSnapshots?: Record<string, unknown>;
  materialsWorkshopDiscovered?: boolean;
  materialsWorkshopRestoration?: 'needs-roof' | 'restored';
  materialsInvestigationSnapshots?: Record<string, unknown>;
  materialTestRecords?: Record<string, {
    completedTestKeys: string[];
    evidenceIds: string[];
    selectedMaterialId?: string;
    completedAt?: number;
  }>;
  evidenceRecords?: Record<string, {
    evidenceIds: string[];
    conclusionId: string;
    completedAt: number;
  }>;
  districtProgress?: Record<string, unknown>;
  completedDistricts?: string[];
  restorationEvents?: string[];
}

export interface WorldState {
  energy: number;         // 0-100
  restored: boolean;
  bridgeSegments: number; // 0-4 for Number Kingdom
  collectibles: string[];
  visited?: boolean;
  currentDistrict?: string;

  // Number Kingdom progression remains flat for save compatibility.
  starBridgeDiscovered?: boolean;
  bridgeCrossingUnlocked?: boolean;
  districtUnlocks?: string[];
  additionCitadelDiscovered?: boolean;
  districtCompletion?: Record<string, boolean>;
  additionCitadelSubArea?: string;
  numberForgeDiscovered?: boolean;
  numberForgeChallengesCompleted?: string[];
  additionCitadelMechanismsPowered?: string[];
  numberKingdomDistrictProgress?: Record<string, unknown>;

  // Story Forest progression is additive and optional for existing V6 saves.
  storyTreeDiscovered?: boolean;
  storyLanternsLit?: string[];
  completedStoryTrails?: string[];
  wordGardenDiscovered?: boolean;
  completedWordGardenChallenges?: string[];
  wordGardenBlooms?: string[];
  characterGroveDiscovered?: boolean;
  memoryTreeDiscovered?: boolean;
  characterGroveChallengeSnapshots?: Record<string, unknown>;
  completedCharacterGroveChallenges?: string[];
  restoredMemoryTrees?: string[];
  characterSkillMastery?: Record<string, string[]>;
  unlockedDistricts?: string[];
  storyForestDistrictProgress?: Record<string, unknown>;
  completedStoryForestDistricts?: string[];
  storyForestRestorationEvents?: string[];

  // Discovery Lab owns a namespaced extension to avoid field collisions.
  discoveryLab?: DiscoveryLabWorldProgress;
}

export interface PlayerProfile {
  name: string;
  xp: number;
  level: number;
  stars: number;
  diamonds: number;
  totalSessions: number;
  lastSession: number;
}

export interface GameSave {
  version: number;
  profile: PlayerProfile;
  mastery: Record<string, SkillMastery>;
  worlds: Record<string, WorldState>;
  settings: {
    reducedMotion: boolean;
    textSize: 'normal' | 'large';
    muted: boolean;
    masterVolume: number;
    musicVolume: number;
    ambienceVolume: number;
    sfxVolume: number;
    parentPin: string;
  };
  lastScene: string;
}

function defaultSave(): GameSave {
  return {
    version: SAVE_VERSION,
    profile: {
      name: 'Star Learner',
      xp: 0,
      level: 1,
      stars: 0,
      diamonds: 0,
      totalSessions: 0,
      lastSession: Date.now(),
    },
    mastery: {},
    worlds: {
      'number-kingdom': {
        energy: 0,
        restored: false,
        bridgeSegments: 0,
        collectibles: [],
        visited: false,
        currentDistrict: 'arrival-valley',
        starBridgeDiscovered: false,
        bridgeCrossingUnlocked: false,
        districtUnlocks: ['arrival-valley'],
        additionCitadelDiscovered: false,
        districtCompletion: {},
        additionCitadelSubArea: 'twin-towers',
        numberForgeDiscovered: false,
        numberForgeChallengesCompleted: [],
        additionCitadelMechanismsPowered: [],
      },
      'story-forest': {
        energy: 0,
        restored: false,
        bridgeSegments: 0,
        collectibles: [],
        visited: false,
        currentDistrict: 'silent-story-tree',
        storyTreeDiscovered: false,
        storyLanternsLit: [],
        completedStoryTrails: [],
        wordGardenDiscovered: false,
        completedWordGardenChallenges: [],
        wordGardenBlooms: [],
        characterGroveDiscovered: false,
        memoryTreeDiscovered: false,
        characterGroveChallengeSnapshots: {},
        completedCharacterGroveChallenges: [],
        restoredMemoryTrees: [],
        characterSkillMastery: {},
        unlockedDistricts: ['silent-story-tree'],
      },
      'discovery-lab':  {
        energy: 0,
        restored: false,
        bridgeSegments: 0,
        collectibles: [],
        visited: false,
        currentDistrict: 'field-campus',
        discoveryLab: {
          fieldCampusDiscovered: false,
          washedOutTrailDiscovered: false,
          investigationsCompleted: [],
          washedOutTrailRestoration: 'damaged',
          unlockedDistricts: ['field-campus'],
          investigationsStarted: [],
          pollinatorGardenDiscovered: false,
          pollinatorGardenRestoration: 'needs-pollinators',
          relationshipInvestigationSnapshots: {},
          materialsWorkshopDiscovered: false,
          materialsWorkshopRestoration: 'needs-roof',
          materialsInvestigationSnapshots: {},
          materialTestRecords: {},
          evidenceRecords: {},
          districtProgress: {},
          completedDistricts: [],
          restorationEvents: [],
        },
      },
    },
    settings: {
      reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
      textSize: 'normal',
      muted: false,
      masterVolume: 1,
      musicVolume: 0.7,
      ambienceVolume: 0.65,
      sfxVolume: 1,
      parentPin: '1234',
    },
    lastScene: 'HomeHub',
  };
}

function migrateFromV5(v5: Record<string, unknown>): Partial<GameSave> {
  // Extract profile data from V5 structure
  try {
    const state = (v5 as { profile?: { name?: string; xp?: number; level?: number; stars?: number } }).profile;
    if (!state) return {};
    return {
      profile: {
        name: state.name ?? 'Star Learner',
        xp: state.xp ?? 0,
        level: state.level ?? 1,
        stars: state.stars ?? 0,
        diamonds: 0,
        totalSessions: 0,
        lastSession: Date.now(),
      },
    };
  } catch { return {}; }
}

class SaveManagerClass {
  private data: GameSave;

  constructor() {
    this.data = this.load();
  }

  private load(): GameSave {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as GameSave;
        if (parsed.version === SAVE_VERSION) {
          const defaults = defaultSave().settings;
          parsed.settings = { ...defaults, ...parsed.settings };
          return parsed;
        }
      }
      // Try V5 migration
      const v5raw = localStorage.getItem(V5_KEY);
      if (v5raw) {
        const v5 = JSON.parse(v5raw) as Record<string, unknown>;
        const migrated = migrateFromV5(v5);
        const save = defaultSave();
        if (migrated.profile) save.profile = migrated.profile;
        return save;
      }
    } catch { /* corrupted, start fresh */ }
    return defaultSave();
  }

  save() {
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(this.data));
    } catch { /* storage full */ }
  }

  get(): GameSave { return this.data; }

  getProfile() { return this.data.profile; }
  getSettings() { return this.data.settings; }
  updateSettings(settings: Partial<GameSave['settings']>) {
    this.data.settings = { ...this.data.settings, ...settings };
    this.save();
    return this.data.settings;
  }
  getWorld(id: string): WorldState {
    if (!this.data.worlds[id]) {
      this.data.worlds[id] = { energy: 0, restored: false, bridgeSegments: 0, collectibles: [] };
    }
    return this.data.worlds[id];
  }

  getMastery(skillId: string): SkillMastery {
    if (!this.data.mastery[skillId]) {
      this.data.mastery[skillId] = { attempts: 0, correct: 0, recent: [], mastery: 0, lastPracticed: 0 };
    }
    return this.data.mastery[skillId];
  }

  recordAnswer(skillId: string, correct: boolean) {
    const m = this.getMastery(skillId);
    m.attempts++;
    if (correct) m.correct++;
    m.recent.push(correct);
    if (m.recent.length > 10) m.recent.shift();
    m.lastPracticed = Date.now();
    // Recalculate mastery from recent performance
    const recentCorrect = m.recent.filter(Boolean).length;
    const recentPct = m.recent.length > 0 ? recentCorrect / m.recent.length : 0;
    const overallPct = m.attempts > 0 ? m.correct / m.attempts : 0;
    m.mastery = Math.round((recentPct * 0.7 + overallPct * 0.3) * 100);
    this.save();
  }

  addXP(amount: number) {
    const p = this.data.profile;
    p.xp += amount;
    // Level up every 200 XP
    const newLevel = Math.floor(p.xp / 200) + 1;
    const leveled = newLevel > p.level;
    p.level = newLevel;
    this.save();
    return leveled;
  }

  addStars(n: number) { this.data.profile.stars += n; this.save(); }
  addDiamonds(n: number) { this.data.profile.diamonds += n; this.save(); }

  updateWorldEnergy(worldId: string, delta: number) {
    const w = this.getWorld(worldId);
    w.energy = Math.min(100, Math.max(0, w.energy + delta));
    this.save();
    return w.energy;
  }

  setBridgeSegments(worldId: string, n: number) {
    this.getWorld(worldId).bridgeSegments = n;
    this.save();
  }

  addCollectible(worldId: string, id: string) {
    const w = this.getWorld(worldId);
    if (!w.collectibles.includes(id)) {
      w.collectibles.push(id);
      this.save();
    }
  }

  setWorldRestored(worldId: string) {
    this.getWorld(worldId).restored = true;
    this.save();
  }

  getLastScene(): string { return this.data.lastScene; }

  setLastScene(scene: string) {
    this.data.lastScene = scene;
    this.save();
  }
}

export const SaveManager = new SaveManagerClass();
